import debug from 'debug';

// Define log levels
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3,
  TRACE = 4,
}

// Define log namespaces for different parts of the application
export const LOG_NAMESPACES = {
  // Core application
  APP: 'rubidex:app',

  // Authentication & Authorization
  AUTH: 'rubidex:auth',

  // API & Network
  API: 'rubidex:api',
  NETWORK: 'rubidex:network',

  // User management
  USER: 'rubidex:user',

  // Sharing profiles
  SHARING: 'rubidex:sharing',

  // Data management
  DATA: 'rubidex:data',
  DATASET: 'rubidex:dataset',

  // UI Components
  UI: 'rubidex:ui',
  FORM: 'rubidex:form',

  // Validation & Security
  VALIDATION: 'rubidex:validation',
  SECURITY: 'rubidex:security',

  // Performance & Monitoring
  PERFORMANCE: 'rubidex:performance',
  QUERY: 'rubidex:query',

  // Development & Debugging
  DEV: 'rubidex:dev',

  // Storage & Cache
  STORAGE: 'rubidex:storage',
  CACHE: 'rubidex:cache',
} as const;

export type LogNamespace = (typeof LOG_NAMESPACES)[keyof typeof LOG_NAMESPACES];

/**
 * Logger class that provides structured logging with different levels and namespaces
 */
export class Logger {
  private readonly errorLogger: debug.Debugger;
  private readonly warnLogger: debug.Debugger;
  private readonly infoLogger: debug.Debugger;
  private readonly debugLogger: debug.Debugger;
  private readonly traceLogger: debug.Debugger;

  private readonly namespace: string;
  private currentLevel: LogLevel;

  constructor(namespace: LogNamespace, level: LogLevel = LogLevel.INFO) {
    this.namespace = namespace;
    this.currentLevel = level;

    // Create debuggers for each level
    this.errorLogger = debug(`${namespace}:error`);
    this.warnLogger = debug(`${namespace}:warn`);
    this.infoLogger = debug(`${namespace}:info`);
    this.debugLogger = debug(`${namespace}:debug`);
    this.traceLogger = debug(`${namespace}:trace`);

    // Set colors for different levels
    this.errorLogger.color = '1'; // Red
    this.warnLogger.color = '3'; // Yellow
    this.infoLogger.color = '4'; // Blue
    this.debugLogger.color = '2'; // Green
    this.traceLogger.color = '5'; // Magenta
  }

  /**
   * Set the minimum log level for this logger instance
   */
  setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }

  /**
   * Log an error message
   */
  error(message: string, ...args: unknown[]): void {
    if (this.currentLevel >= LogLevel.ERROR) {
      this.errorLogger(this.formatMessage(message), ...args);

      // Also log to console.error in development for better visibility
      if (import.meta.env.DEV) {
        console.error(`[${this.namespace}:error]`, message, ...args);
      }
    }
  }

  /**
   * Log a warning message
   */
  warn(message: string, ...args: unknown[]): void {
    if (this.currentLevel >= LogLevel.WARN) {
      this.warnLogger(this.formatMessage(message), ...args);

      if (import.meta.env.DEV) {
        console.warn(`[${this.namespace}:warn]`, message, ...args);
      }
    }
  }

  /**
   * Log an info message
   */
  info(message: string, ...args: unknown[]): void {
    if (this.currentLevel >= LogLevel.INFO) {
      this.infoLogger(this.formatMessage(message), ...args);

      if (import.meta.env.DEV) {
        console.log(`[${this.namespace}:info]`, message, ...args);
      }
    }
  }

  /**
   * Log a debug message
   */
  debug(message: string, ...args: unknown[]): void {
    if (this.currentLevel >= LogLevel.DEBUG) {
      this.debugLogger(this.formatMessage(message), ...args);
    }
  }

  /**
   * Log a trace message (most verbose)
   */
  trace(message: string, ...args: unknown[]): void {
    if (this.currentLevel >= LogLevel.TRACE) {
      this.traceLogger(this.formatMessage(message), ...args);
    }
  }

  /**
   * Log API requests and responses
   */
  api(method: string, url: string, data?: unknown, response?: unknown): void {
    if (this.currentLevel >= LogLevel.DEBUG) {
      this.debugLogger(`${method.toUpperCase()} ${url}`, { data, response });
    }
  }

  /**
   * Log performance metrics
   */
  performance(operation: string, duration: number, metadata?: unknown): void {
    if (this.currentLevel >= LogLevel.INFO) {
      this.infoLogger(`Performance: ${operation} took ${duration}ms`, metadata);
    }
  }

  /**
   * Log validation errors with context
   */
  validation(field: string, error: string, value?: unknown): void {
    if (this.currentLevel >= LogLevel.WARN) {
      this.warnLogger(`Validation failed for ${field}: ${error}`, { value });
    }
  }

  /**
   * Log security-related events
   */
  security(event: string, details?: unknown): void {
    // Security events should always be logged
    this.errorLogger(`Security Event: ${event}`, details);

    if (import.meta.env.DEV) {
      console.warn(`[SECURITY] ${this.namespace}:`, event, details);
    }
  }

  /**
   * Format message with timestamp in development
   */
  private formatMessage(message: string): string {
    if (import.meta.env.DEV) {
      const timestamp = new Date().toISOString();
      return `[${timestamp}] ${message}`;
    }
    return message;
  }

  /**
   * Create a child logger with additional namespace
   */
  child(subNamespace: string): Logger {
    return new Logger(`${this.namespace}:${subNamespace}` as LogNamespace, this.currentLevel);
  }
}

/**
 * Create a logger instance for a specific namespace
 */
export function createLogger(namespace: LogNamespace, level?: LogLevel): Logger {
  return new Logger(namespace, level);
}

/**
 * Get the current log level from environment variables
 */
export function getLogLevelFromEnv(): LogLevel {
  const envLevel = import.meta.env.VITE_LOG_LEVEL?.toUpperCase();

  switch (envLevel) {
    case 'ERROR':
      return LogLevel.ERROR;
    case 'WARN':
      return LogLevel.WARN;
    case 'INFO':
      return LogLevel.INFO;
    case 'DEBUG':
      return LogLevel.DEBUG;
    case 'TRACE':
      return LogLevel.TRACE;
    default:
      return import.meta.env.DEV ? LogLevel.DEBUG : LogLevel.INFO;
  }
}

/**
 * Check if logging should be enabled based on environment and API host
 */
function shouldEnableLogging(): boolean {
  // Always enable in development mode
  if (import.meta.env.DEV) {
    return true;
  }

  // Enable if using dev API host
  try {
    const apiHost = window.location.host.includes('localhost')
      ? import.meta.env.VITE_RUBIDEX_BE || 'http://localhost'
      : `https://${window.location.host}`;

    if (apiHost === 'https://dev.rubidex.ai' || apiHost.includes('localhost') || apiHost === 'https://qa.rubidex.ai') {
      return true;
    }
  } catch (_error) {
    // Fallback if window is not available (SSR, tests, etc.)
    console.warn('[Logger] Could not determine API host for logging configuration');
  }

  return false;
}

/**
 * Configure debug namespaces based on environment
 */
export function configureLogging(): void {
  const logLevel = getLogLevelFromEnv();
  const loggingEnabled = shouldEnableLogging();

  if (loggingEnabled) {
    // Enable all rubidex namespaces when logging is active
    debug.enabled = (name: string) => {
      return name.startsWith('rubidex:');
    };

    if (import.meta.env.DEV) {
      console.log(`[Logger] Development mode - Log level: ${LogLevel[logLevel]}`);
    } else {
      console.log(`[Logger] Dev API detected - Log level: ${LogLevel[logLevel]}`);
    }
    console.log('[Logger] Available namespaces:', Object.values(LOG_NAMESPACES));
  } else {
    // In production with non-dev API, only enable critical logs
    const enabledNamespaces = ['rubidex:*:error', 'rubidex:security:*'];

    debug.enabled = (name: string) => {
      return enabledNamespaces.some((pattern) => {
        const regex = new RegExp(pattern.replace(/\*/g, '.*'));
        return regex.test(name);
      });
    };

    console.log('[Logger] Production mode - Limited logging enabled');
  }
}

// Default logger instances for common use cases
export const appLogger = createLogger(LOG_NAMESPACES.APP, getLogLevelFromEnv());
export const authLogger = createLogger(LOG_NAMESPACES.AUTH, getLogLevelFromEnv());
export const apiLogger = createLogger(LOG_NAMESPACES.API, getLogLevelFromEnv());
export const validationLogger = createLogger(LOG_NAMESPACES.VALIDATION, getLogLevelFromEnv());
export const securityLogger = createLogger(LOG_NAMESPACES.SECURITY, LogLevel.ERROR);

// Initialize logging configuration
configureLogging();
